<script setup lang="ts" generic="K extends string">
/*
 * Segmented pill control. The active tab is a solid brand-violet rounded pill
 * (white text); inactive tabs are plain text. Each tab may carry an optional
 * item-count badge — the badge is hidden when the count is null/undefined or 0
 * ("hide-at-unknown"), so counts that aren't available yet simply omit it.
 *
 * `size` controls density: 'md' (default) or 'sm' for compact inline use (e.g.
 * the Job Grades currency selector). v-model selects the active tab by `key`.
 * Renders an ARIA tablist for AT.
 */
interface SegmentedTab<T extends string> {
  key: T;
  label: string;
  /** Optional count badge. Null/undefined/0 → no badge shown. */
  count?: number | null;
}

withDefaults(
  defineProps<{
    modelValue: K;
    tabs: SegmentedTab<K>[];
    /** Accessible label for the tablist group. */
    ariaLabel?: string;
    /** Density. 'sm' is a more compact pill (smaller text + padding). */
    size?: 'md' | 'sm';
  }>(),
  { size: 'md' },
);

defineEmits<{ 'update:modelValue': [key: K] }>();
</script>

<template>
  <div
    :class="[
      'inline-flex flex-wrap items-center rounded-xl border border-app-border bg-app-bg',
      size === 'sm' ? 'gap-0.5 p-0.5' : 'gap-1 p-1',
    ]"
    role="tablist"
    :aria-label="ariaLabel"
    data-testid="segmented-tabs"
  >
    <button
      v-for="tab in tabs"
      :key="tab.key"
      type="button"
      role="tab"
      :aria-selected="modelValue === tab.key"
      :data-testid="`segmented-tab-${tab.key}`"
      :class="[
        'inline-flex cursor-pointer items-center whitespace-nowrap rounded-lg font-semibold transition-colors',
        size === 'sm' ? 'gap-1.5 px-2.5 py-1 text-xs' : 'gap-2 px-3.5 py-1.5 text-[13px]',
        modelValue === tab.key
          ? 'bg-primary text-white shadow-[0_2px_8px_rgba(91,79,232,0.22)]'
          : 'text-app-fg hover:bg-app-border-soft',
      ]"
      @click="$emit('update:modelValue', tab.key)"
    >
      {{ tab.label }}
      <span
        v-if="tab.count != null && tab.count > 0"
        :data-testid="`segmented-tab-count-${tab.key}`"
        :class="[
          'inline-flex items-center justify-center rounded-full font-bold tabular-nums',
          size === 'sm'
            ? 'min-w-[1rem] px-1 py-0.5 text-[10px]'
            : 'min-w-[1.25rem] px-1.5 py-0.5 text-[11px]',
          modelValue === tab.key ? 'bg-white/25 text-white' : 'bg-app-border-soft text-app-muted',
        ]"
      >
        {{ tab.count }}
      </span>
    </button>
  </div>
</template>
